import React, { useState, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const DocumentUploadModal = ({ isOpen, onClose, onUpload }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    // Validate event object
    if (!event || !event?.target || !event?.target?.files) {
      console.error('Invalid event object in handleFileSelect:', event);
      setError('Fehler beim Datei-Upload. Bitte versuchen Sie es erneut.');
      return;
    }

    const file = event?.target?.files?.[0];
    if (file) {
      // Accept both images and PDFs
      if (file?.type?.startsWith('image/') || file?.type === 'application/pdf') {
        setSelectedFile(file);
        
        // Only create preview URL for images, not PDFs
        if (file?.type?.startsWith('image/')) {
          setPreviewUrl(URL.createObjectURL(file));
        } else {
          setPreviewUrl(null); // No preview for PDFs
        }
        
        setError('');
      } else {
        setError('Bitte wählen Sie eine Bilddatei (JPG, PNG) oder PDF aus');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Bitte laden Sie ein Dokument hoch');
      return;
    }

    setIsProcessing(true);
    
    const uploadData = {
      file: selectedFile,
      fileName: selectedFile?.name,
      uploadDate: new Date()?.toISOString(),
      chassisNumber: null, // AI extraction removed - no automatic chassis detection
      extractionConfidence: 0,
      extractionData: null
    };

    if (onUpload) {
      onUpload(uploadData);
    }

    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError('');
    setIsProcessing(false);
    if (onClose) {
      onClose();
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      handleFileSelect({ target: { files: [file] } });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-lg shadow-modal max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-heading font-semibold text-foreground">
            Fahrzeugdokument hochladen
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Schließen"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {!selectedFile ? (
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary transition-smooth cursor-pointer"
              onClick={() => fileInputRef?.current?.click()}
            >
              <Icon name="Upload" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-lg font-medium text-foreground mb-2">
                Dokument hochladen oder hierher ziehen
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Unterstützte Formate: JPG, PNG, PDF (max. 10MB)
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  iconName="Camera"
                  iconPosition="left"
                  className="md:hidden"
                  onClick={(e) => {
                    e?.stopPropagation();
                    cameraInputRef?.current?.click();
                  }}
                >
                  Kamera
                </Button>
                <Button
                  variant="default"
                  iconName="FolderOpen"
                  iconPosition="left"
                  onClick={(e) => {
                    e?.stopPropagation();
                    fileInputRef?.current?.click();
                  }}
                >
                  Datei auswählen
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-muted">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Dokumentvorschau"
                    className="w-full h-64 object-contain"
                  />
                ) : (
                  <div className="w-full h-64 flex flex-col items-center justify-center">
                    <Icon name="FileText" size={64} color="var(--color-muted-foreground)" className="mb-4" />
                    <p className="text-sm font-medium text-foreground">{selectedFile?.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">PDF-Dokument</p>
                  </div>
                )}
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl(null);
                    setError('');
                  }}
                  className="absolute top-2 right-2 p-2 bg-card rounded-lg shadow-card hover:bg-muted transition-smooth"
                  aria-label="Dokument entfernen"
                >
                  <Icon name="Trash2" size={18} color="var(--color-error)" />
                </button>
              </div>

              {/* Info Notice when no extraction attempted yet */}
              {!error && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} color="var(--color-blue-500)" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">Hinweis zur Fahrgestellnummer</p>
                      <p className="text-xs text-muted-foreground">
                        Die Fahrgestellnummer (FIN) wird automatisch aus dem Dokument extrahiert.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-4 rounded-lg bg-error/10 border border-error/20">
                  <Icon 
                    name="AlertCircle" 
                    size={20} 
                    color="var(--color-error)" 
                    className="flex-shrink-0 mt-0.5" 
                  />
                  <div className="flex-1">
                    <p className="text-sm text-error">
                      {error}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={handleClose} iconName="">
            Abbrechen
          </Button>
          <Button
            variant="default"
            onClick={handleUpload}
            disabled={!selectedFile || isProcessing}
            loading={isProcessing}
            iconName="Upload"
            iconPosition="left"
          >
            Hochladen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;